import { useCallback, useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, rupiah, STATUS, tableLabel } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import { todayWIB, fmtClock } from "@/components/staff/attendance/utils";
import { MonthlyReport } from "@/components/staff/MonthlyReport";
import { ReportRecipients } from "@/components/staff/ReportRecipients";
import { PromoIdeas } from "@/components/staff/PromoIdeas";
import { PromoResults } from "@/components/staff/PromoResults";
import { ClosingHistory } from "@/components/staff/ClosingHistory";

export const ReportsPanel = () => {
  const [mode, setMode] = useState("harian");
  const [outlets, setOutlets] = useState([]);
  useEffect(() => { api.get("/admin/outlets").then((r) => setOutlets(r.data)).catch(() => {}); }, []);
  const btn = (m, t) => (
    <button onClick={() => setMode(m)} className={`px-4 py-2 text-sm rounded-full font-semibold ${mode === m ? "bg-forest text-white" : "bg-sage-100 text-ink"}`} data-testid={`report-mode-${m}`}>{t}</button>
  );
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">{btn("harian", "Harian")}{btn("bulanan", "Bulanan & grafik")}{btn("tutup", "Tutup kasir")}{btn("promo", "Ide promo")}{btn("hasil", "Hasil promo")}{btn("email", "Email otomatis")}</div>
      {mode === "promo" && <PromoIdeas outlets={outlets} />}
      {mode === "hasil" && <PromoResults />}
      {mode === "harian" && <DailyReport outlets={outlets} />}
      {mode === "bulanan" && <MonthlyReport outlets={outlets} />}
      {mode === "tutup" && <ClosingHistory outlets={outlets} />}
      {mode === "email" && <ReportRecipients outlets={outlets} />}
    </div>
  );
};

const Row = ({ label, value, strong }) => (
  <div className={`flex justify-between py-1 ${strong ? "font-bold border-t border-sage-200 mt-1 pt-2" : "text-muted2"}`}><span>{label}</span><span className="text-ink">{value}</span></div>
);

export const OutletCard = ({ s, children }) => (
  <div className="card-paper p-5 text-sm" data-testid={`report-outlet-${s.outlet_code}`}>
    <div className="font-semibold text-base">{s.outlet_code} · Gerai {s.outlet_name}</div>
    <div className="text-xs text-muted2 mb-2">{s.orders_paid} lunas · {s.orders_pending} belum lunas · {s.orders_cancelled} batal</div>
    <Row label="Subtotal" value={rupiah(s.subtotal)} />
    <Row label={`Potongan Coin (${s.coins_used})`} value={`-${rupiah(s.discount)}`} />
    <Row label="DPP" value={rupiah(s.dpp)} />
    <Row label="PB1" value={rupiah(s.tax)} />
    <Row label="Kode unik" value={rupiah(s.unique_code)} />
    <Row label="Total diterima" value={rupiah(s.total)} strong />
    <Row label="· via QRIS" value={rupiah(s.qris)} />
    <Row label="· via Transfer Bank" value={rupiah(s.bank)} />
    {children}
  </div>
);

export const DailyReport = ({ outlets }) => {
  const [date, setDate] = useState(todayWIB());
  const [outlet, setOutlet] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/reports/daily", { params: { date, outlet } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [date, outlet]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const header = ["Tanggal", "Gerai", "No. Pesanan", "Jam", "Lokasi", "Status", "Metode", "Subtotal", "Potongan Coin", "DPP", "Pajak", "Tarif Pajak", "Kode Unik", "Total", "Pelanggan"];
    const paidSet = ["diproses", "diantar", "selesai"];
    const rows = data.orders.map((o) => {
      const paid = paidSet.includes(o.status);
      const n = (v) => (paid ? v : "");
      return [data.date, o.outlet_code, o.code, fmtClock(o.created_at), tableLabel(o.floor, o.table), STATUS[o.status].label,
        o.payment_method === "qris" ? "QRIS" : "Transfer Bank", n(o.subtotal), n(o.discount), n(o.dpp), n(o.tax_amount), `${o.tax_name} ${o.tax_rate}%`, n(o.unique_code), n(o.total), o.customer_name];
    });
    rows.push([]);
    rows.push(["REKAP GERAI (hanya pesanan lunas)"]);
    data.outlets.forEach((s) => rows.push([data.date, s.outlet_code, "TOTAL LUNAS", "", "", `${s.orders_paid} pesanan`, `QRIS ${s.qris} / Bank ${s.bank}`, s.subtotal, s.discount, s.dpp, s.tax, "", s.unique_code, s.total, ""]));
    downloadCsv(`laporan-${outlet || "semua-gerai"}-${data.date}.csv`, header, rows);
  };

  return (
    <div data-testid="daily-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif font-semibold text-2xl">Laporan harian gerai</h2>
          <p className="text-sm text-muted2">Penjualan & pajak per gerai. Hanya pesanan lunas yang dijumlahkan.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="px-3 py-2 rounded-lg border border-sage-200 bg-paper text-sm" data-testid="report-date-input" />
          <select value={outlet} onChange={(e) => setOutlet(e.target.value)} className="px-3 py-2 rounded-lg border border-sage-200 bg-paper text-sm" data-testid="report-outlet-filter">
            <option value="">Semua gerai</option>
            {outlets.map((o) => <option key={o.code} value={o.code}>{o.code} · {o.name}</option>)}
          </select>
          <button onClick={exportCsv} disabled={!data || !data.orders.length} className="btn-forest px-4 py-2 text-sm flex items-center gap-2" data-testid="report-download-button"><Download className="w-4 h-4" /> Unduh CSV</button>
        </div>
      </div>
      {data && data.outlets.length === 0 && <p className="text-center text-muted2 py-12" data-testid="report-empty">Belum ada transaksi pada tanggal ini.</p>}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">{data?.outlets.map((s) => <OutletCard key={s.outlet_code} s={s} />)}</div>
      {data?.orders.length > 0 && (
        <div className="card-paper mt-5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="report-orders-table">
            <thead><tr className="text-left text-xs text-muted2 border-b border-sage-200">{["No. Pesanan", "Jam", "Status", "Metode", "DPP", "Pajak", "Total"].map((h) => <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-sage-200">
              {data.orders.map((o) => (
                <tr key={o.id} data-testid={`report-order-${o.id}`}>
                  <td className="px-4 py-2.5 font-semibold whitespace-nowrap">{o.code}</td><td className="px-4 py-2.5">{fmtClock(o.created_at)}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{STATUS[o.status].label}</td><td className="px-4 py-2.5">{o.payment_method === "qris" ? "QRIS" : "Bank"}</td>
                  <td className="px-4 py-2.5">{rupiah(o.dpp)}</td><td className="px-4 py-2.5">{rupiah(o.tax_amount)}</td><td className="px-4 py-2.5 font-semibold">{rupiah(o.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
